import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MetadataService } from '../../../../../../services/metadata.service';
import { TreeBuilder } from '../column-picker/tree-builder';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { startWith, Subscription, tap } from 'rxjs';
import { ColumnEmitted, Tree } from '../column-picker/models/column-picker';
import { ColumnItemComponent } from './components/column-item/column-item.component';
import { ColumnPickerComponent } from '../column-picker/column-picker.component';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { TabColumn } from '../../../../models/utils-tabs';
import { TableType } from '../../../../../../models/shipment';
import { MetadataColumn } from '../../../../../../models/metadata';

interface ColumnsForm {
  order: number;
  columnKey: string;
}

@Component({
  selector: 'app-columns',
  standalone: true,
  templateUrl: './columns.component.html',
  imports: [ColumnItemComponent, ColumnPickerComponent, CdkDrag, CdkDropList, CdkDragPlaceholder],
  styleUrl: './columns.component.scss'
})
export class ColumnsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() columnArray!: FormArray<FormGroup>;
  @Input() columns?: TabColumn[];
  @Input() type!: TableType;
  treeNodes: Tree[] = [];

  private allColumns: MetadataColumn[] = [];
  private treeBuilder = new TreeBuilder();
  private subscription = new Subscription();

  constructor(private metadataService: MetadataService) {}

  ngOnInit() {
    if (this.columns) {
      this.columns.forEach(({ columnKey, order, columnFormatKey }) =>
        this.addNewColumn(columnKey, order, columnFormatKey)
      );
    } else {
      const defaultColumnKeys: Partial<Record<TableType, string>> = {
        Shipments: 'Header.ShipmentNo',
        Containers: 'Shipment.Goods.ContainerNo',
        Orders: 'Order.Header.OrderNo',
        OrderExceptions: 'OrderException.Id'
      };
      this.addNewColumn(defaultColumnKeys[this.type] || '');
    }
    this.subscription.add(
      this.columnArray.valueChanges
        .pipe(
          startWith(this.columnArray.getRawValue()),
          tap(formData => {
            this.treeNodes = this.updateColumnAvailability(formData as unknown as ColumnsForm[]);
          })
        )
        .subscribe()
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columnArray']) {
      this.subscription.add(
        this.metadataService
          .getFilteredOutMetadata()
          .pipe(
            tap(({ columnGroups, shipmentColumns, containerColumns, orderColumns, orderExceptionColumns }) => {
              const columnsConfig: { [key: string]: MetadataColumn[] } = {
                Shipments: shipmentColumns,
                Containers: containerColumns,
                Orders: orderColumns,
                OrderExceptions: orderExceptionColumns
              };
              const columns = columnsConfig[this.type];
              this.allColumns = columns;
              this.treeNodes = this.treeBuilder.buildTree(columnGroups, columns);
            })
          )
          .subscribe()
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onColumnSelected({ key }: ColumnEmitted) {
    this.addNewColumn(key);
  }

  addNewColumn(columnKey: string | null, order?: number, columnFormatKey?: string) {
    const numberOfColumns = this.columnArray.controls.length;
    const newGroup = new FormGroup<{
      order: FormControl<number | null>;
      columnKey: FormControl<string | null>;
      columnFormatKey?: FormControl<string | null>;
    }>({
      order: new FormControl(order ?? numberOfColumns),
      columnKey: new FormControl(columnKey || null, Validators.required)
    });
    const isDateTime = this.allColumns.find(col => col.key === columnKey)?.dataType === 'DateTime';
    if (isDateTime) {
      newGroup.addControl('columnFormatKey', new FormControl(columnFormatKey || 'DateTime'));
    }
    this.columnArray.push(newGroup);
  }

  deleteColumn(index: number) {
    this.columnArray.removeAt(index);
    this.columnArray.updateValueAndValidity();
    this.reorderColumns();
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<any>) {
    moveItemInArray(this.columnArray.controls, previousIndex, currentIndex);
    this.columnArray.controls.forEach((control, index) => control.patchValue({ order: index }));
  }

  private updateColumnAvailability(formData: ColumnsForm[], nodes?: Tree[]): Tree[] {
    const selectedColumnKeys = formData.map(({ columnKey }) => columnKey);
    const source = nodes ? nodes : this.treeNodes;
    return source.map(node => {
      if (node.children && node.children.length > 0) {
        return { ...node, children: this.updateColumnAvailability(formData, node.children) };
      } else {
        return {
          ...node,
          disabled: selectedColumnKeys.includes(node.key)
        };
      }
    });
  }

  private reorderColumns() {
    this.columnArray.controls.forEach((control, index) => {
      control.get('order')?.setValue(index);
    });
  }
}
